import React from 'react';
import { useSelector } from 'react-redux';
import CountUp from '../Counter/count-up-component';
import { getLandingData } from '../../Public/landing-data';

/**
 * MetricsSection, 1T Solutions theme
 * Dark navy strip with large animated numbers separated by subtle vertical lines.
 * No header / logo, purely the numbers.
 */
function MetricsSection({ metrics }) {
    const systemData = useSelector((state) => state.systemData || {});
    const { businessInfo = {}, dir, language } = systemData;

    const landingData = getLandingData(language);
    const { metricsSection } = landingData || {};

    let metricsToDisplay = [];
    if (metrics && metrics.length > 0) {
        metricsToDisplay = metrics;
    } else if (businessInfo.metrics && businessInfo.metrics.length > 0) {
        metricsToDisplay = businessInfo.metrics;
    } else {
        metricsToDisplay = metricsSection?.metrics || [];
    }

    if (!metricsToDisplay.length) return null;

    const isRtl = dir === 'rtl' || language === 'he';
    const provenLabel = isRtl ? 'תוצאות מוכחות' : 'Proven Results';

    return (
        <div dir={dir} className="w-full relative overflow-hidden">

            {/* Red accent top line */}
            <div className="absolute top-0 left-0 right-0 h-[3px] bg-secondary-500 z-10" />

            {/* Dot-grid texture */}
            <div
                className="absolute inset-0 pointer-events-none opacity-[0.04] z-0"
                style={{ backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '24px 24px' }}
            />

            {/* Label */}
            <div className={`relative z-10 pt-8 px-6 ${isRtl ? 'text-right' : 'text-left'} max-w-7xl mx-auto`}>
                <span className="text-[10px] font-black tracking-[0.4em] text-secondary-500/80 uppercase">
                    {provenLabel}
                </span>
            </div>

            {/* Metrics row */}
            <div className="relative z-10 max-w-7xl mx-auto px-6 py-8 lg:py-10">
                <div className="flex flex-wrap lg:flex-nowrap">
                    {metricsToDisplay.map((m, idx) => (
                        <div
                            key={idx}
                            className={[
                                'flex flex-col items-center justify-center text-center',
                                'w-1/2 lg:flex-1 py-6 px-4 lg:px-8',
                                idx > 0 ? 'border-l border-white/10' : '',
                                // top border for wrapped rows on mobile
                                idx >= 2 ? 'border-t border-white/10 lg:border-t-0' : '',
                            ].join(' ')}
                        >
                            {/* Number */}
                            <div className="flex items-end gap-1 justify-center" dir="ltr">
                                <CountUp
                                    from={0}
                                    to={m.value}
                                    direction="up"
                                    duration={m.duration || 2.2}
                                    delay={idx * 0.15}
                                    className="text-6xl md:text-7xl lg:text-8xl font-black text-secondary-500 leading-none tabular-nums"
                                />
                                {m.suffix && (
                                    <span className="text-3xl lg:text-4xl font-black text-secondary-500 leading-none mb-1">
                                        {m.suffix}
                                    </span>
                                )}
                            </div>

                            {/* Label */}
                            <p className="mt-3 text-xs md:text-sm font-bold uppercase tracking-wider text-white/55 max-w-[140px] leading-snug">
                                {m.label}
                            </p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Disclaimer */}
            {metricsSection?.disclaimer && (
                <p className="relative z-10 text-center text-xs text-white/30 font-medium pb-6 px-6">
                    {metricsSection.disclaimer}
                </p>
            )}
        </div>
    );
}

export default MetricsSection;
